import {isPlatformBrowser, NgClass, NgForOf, NgIf} from '@angular/common';
import {ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, Input, OnInit, PLATFORM_ID} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {
    PublicTripCalculatorDefaults,
    PublicTripFuelType,
    PublicTripVehicleClass,
} from '../../../interfaces/public-trip-calculator';
import {PublicTripCalculatorService} from '../../../services/public-trip-calculator.service';
import {AnalyticsService} from '../../../services/analytics.service';
import {RouteResult} from '../../../interfaces/route';
import {RoutePlannerComponent} from '../route-planner/route-planner.component';

interface TripVehicleClassOption {
    id: PublicTripVehicleClass;
    label: string;
    description: string;
}

interface TripFuelTypeOption {
    id: PublicTripFuelType;
    label: string;
}

interface TripFallbackDefaults {
    fuelPrice: number;
    consumption: number;
}

type TripDistanceMode = 'manual' | 'route';

interface TripCalculatorPersistedState {
    vehicleClass?: PublicTripVehicleClass;
    fuelType?: PublicTripFuelType;
    distance?: number;
    routeDistance?: number;
    distanceMode?: TripDistanceMode;
    roundTrip?: boolean;
    fuelPrice?: number;
    consumption?: number;
}

const STORAGE_KEY = 'motordesk.publicTripCalculator.state.v1';

const VEHICLE_CLASS_OPTIONS: TripVehicleClassOption[] = [
    {
        id: 'passenger',
        label: 'Легковой автомобиль',
        description: 'Седан, хэтчбек, универсал',
    },
    {
        id: 'crossover',
        label: 'Кроссовер',
        description: 'SUV и городские кроссоверы',
    },
];

const FUEL_TYPE_OPTIONS: TripFuelTypeOption[] = [
    {
        id: 'petrol',
        label: 'Бензин',
    },
    {
        id: 'diesel',
        label: 'Дизель',
    },
];

const FALLBACK_DEFAULTS: Record<PublicTripVehicleClass, Record<PublicTripFuelType, TripFallbackDefaults>> = {
    passenger: {
        petrol: {
            fuelPrice: 75,
            consumption: 8.2,
        },
        diesel: {
            fuelPrice: 80,
            consumption: 6.8,
        },
    },
    crossover: {
        petrol: {
            fuelPrice: 75,
            consumption: 10.4,
        },
        diesel: {
            fuelPrice: 80,
            consumption: 8.6,
        },
    },
};

@Component({
    selector: 'app-public-trip-calculator',
    standalone: true,
    imports: [
        FormsModule,
        NgClass,
        NgForOf,
        NgIf,
        RoutePlannerComponent,
    ],
    templateUrl: './public-trip-calculator.component.html',
    styleUrl: './public-trip-calculator.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PublicTripCalculatorComponent implements OnInit {
    @Input() compact = false;
    @Input() context: 'landing' | 'blog' | 'fuel' = 'landing';

    protected readonly vehicleClassOptions = VEHICLE_CLASS_OPTIONS;
    protected readonly fuelTypeOptions = FUEL_TYPE_OPTIONS;
    vehicleClass: PublicTripVehicleClass = 'passenger';
    fuelType: PublicTripFuelType = 'petrol';
    distance = 100;
    routeDistance = 0;
    distanceMode: TripDistanceMode = 'manual';
    routeModeInitialized = false;
    roundTrip = false;
    fuelPrice = FALLBACK_DEFAULTS.passenger.petrol.fuelPrice;
    consumption = FALLBACK_DEFAULTS.passenger.petrol.consumption;
    defaults: PublicTripCalculatorDefaults | null = null;
    loading = false;
    loadFailed = false;

    constructor(
        private calculatorService: PublicTripCalculatorService,
        private changeDetectorRef: ChangeDetectorRef,
        private analytics: AnalyticsService,
        @Inject(PLATFORM_ID) private platformId: object,
    ) {
    }

    ngOnInit(): void {
        const restored = this.restoreState();
        this.routeModeInitialized = this.distanceMode === 'route';
        this.loadDefaults(restored);
    }

    onVehicleClassChange(value: PublicTripVehicleClass): void {
        this.trackCalculatorUse();
        this.vehicleClass = this.normalizeVehicleClass(value);
        this.applyLocalFallback();
        this.loadDefaults();
        this.persistState();
    }

    onFuelTypeChange(value: PublicTripFuelType): void {
        this.trackCalculatorUse();
        this.fuelType = this.normalizeFuelType(value);
        this.applyLocalFallback();
        this.loadDefaults();
        this.persistState();
    }

    onFuelPriceChange(value: number | string): void {
        this.trackCalculatorUse();
        this.fuelPrice = this.normalizePositiveNumber(value);
        this.persistState();
    }

    onConsumptionChange(value: number | string): void {
        this.trackCalculatorUse();
        this.consumption = this.normalizePositiveNumber(value);
        this.persistState();
    }

    onDistanceChange(value: number | string): void {
        this.trackCalculatorUse();
        this.distance = this.normalizePositiveNumber(value);
        this.persistState();
    }

    onDistanceModeChange(value: TripDistanceMode): void {
        const nextMode: TripDistanceMode = value === 'route' ? 'route' : 'manual';
        if (this.distanceMode === nextMode) {
            return;
        }

        this.trackCalculatorUse();
        this.distanceMode = nextMode;
        if (nextMode === 'route') {
            this.routeModeInitialized = true;
        }
        this.persistState();
        this.analytics.goal('trip_distance_mode_changed', {
            context: this.context,
            mode: nextMode,
        });
        this.changeDetectorRef.markForCheck();
    }

    onRoundTripChange(value: boolean): void {
        this.trackCalculatorUse();
        this.roundTrip = value;
        this.persistState();
    }

    onRouteCalculated(result: RouteResult): void {
        this.routeDistance = this.normalizePositiveNumber(result.distance_km);
        this.roundTrip = result.round_trip;
        this.persistState();
        this.trackCalculatorUse();
        this.analytics.goal('trip_distance_autofilled', {
            context: this.context,
            round_trip: result.round_trip,
            cached: result.cached,
        });
        this.changeDetectorRef.markForCheck();
    }

    onRouteInvalidated(): void {
        if (this.routeDistance === 0) {
            return;
        }

        this.routeDistance = 0;
        this.persistState();
        this.changeDetectorRef.markForCheck();
    }

    onDistanceFocus(event: FocusEvent): void {
        this.selectInputContent(event);
    }

    onFuelPriceFocus(event: FocusEvent): void {
        this.selectInputContent(event);
    }

    onConsumptionFocus(event: FocusEvent): void {
        this.selectInputContent(event);
    }

    effectiveDistance(): number {
        const distance = this.distanceMode === 'route'
            ? this.normalizePositiveNumber(this.routeDistance)
            : this.normalizePositiveNumber(this.distance);

        return this.roundTrip ? distance * 2 : distance;
    }

    estimatedLitres(): number {
        const distance = this.effectiveDistance();
        const consumption = this.normalizePositiveNumber(this.consumption);

        if (distance <= 0 || consumption <= 0) {
            return 0;
        }

        return this.roundFuel((consumption * distance) / 100);
    }

    estimatedCost(): number {
        const litres = this.estimatedLitres();
        const fuelPrice = this.normalizePositiveNumber(this.fuelPrice);

        if (litres <= 0 || fuelPrice <= 0) {
            return 0;
        }

        return this.roundMoney(litres * fuelPrice);
    }

    costPerKm(): number {
        const distance = this.effectiveDistance();

        if (distance <= 0) {
            return 0;
        }

        return this.roundMoney(this.estimatedCost() / distance);
    }

    costPer100Km(): number {
        const consumption = this.normalizePositiveNumber(this.consumption);
        const fuelPrice = this.normalizePositiveNumber(this.fuelPrice);

        if (consumption <= 0 || fuelPrice <= 0) {
            return 0;
        }

        return this.roundMoney(consumption * fuelPrice);
    }

    hasCalculation(): boolean {
        return this.effectiveDistance() > 0
            && this.normalizePositiveNumber(this.consumption) > 0
            && this.normalizePositiveNumber(this.fuelPrice) > 0;
    }

    fuelPriceSourceLabel(): string {
        const source = this.defaults?.fuel_price_source;

        switch (source) {
            case 'month_average':
                return 'средняя цена по данным MotorDesk за последний месяц';
            case 'last_value':
                return 'последняя найденная цена в данных MotorDesk';
            case 'fallback':
                return 'резервное значение, которое можно изменить вручную';
            default:
                return this.loadFailed
                    ? 'резервное значение, API сейчас недоступен'
                    : 'подставляем среднее значение';
        }
    }

    fuelPriceSourceTone(): string {
        const source = this.defaults?.fuel_price_source;

        if (this.loadFailed || source === 'fallback') {
            return 'warning';
        }

        if (source === 'last_value') {
            return 'muted';
        }

        return 'success';
    }

    sourcePeriodLabel(): string {
        const dateFrom = this.defaults?.fuel_price_period?.date_from;
        const dateTo = this.defaults?.fuel_price_period?.date_to;

        if (!dateFrom && !dateTo) {
            return 'период не определен';
        }

        if (dateFrom && dateTo && dateFrom !== dateTo) {
            return `${dateFrom} - ${dateTo}`;
        }

        return dateFrom || dateTo || 'период не определен';
    }

    recordsCountLabel(): string {
        const count = this.defaults?.records_count || 0;

        if (count === 0) {
            return 'без записей в выборке';
        }

        if (count < 10) {
            return 'свежие данные MotorDesk';
        }

        return `${count.toLocaleString('ru-RU')} ${this.pluralize(count, 'запись', 'записи', 'записей')}`;
    }

    formatNumber(value: number, minimumFractionDigits = 0, maximumFractionDigits = 0): string {
        return new Intl.NumberFormat('ru-RU', {
            minimumFractionDigits,
            maximumFractionDigits,
        }).format(value);
    }

    formatMoney(value: number, minimumFractionDigits = 0, maximumFractionDigits = 0): string {
        return `${this.formatNumber(value, minimumFractionDigits, maximumFractionDigits)} ₽`;
    }

    contextTitle(): string {
        if (this.context === 'blog') {
            return 'Быстро прикиньте топливную часть поездки';
        }
        if (this.context === 'fuel') {
            return 'Рассчитайте топливо и стоимость маршрута';
        }
        return 'Онлайн-калькулятор поездки по средним данным MotorDesk';
    }

    contextLead(): string {
        if (this.context === 'blog') {
            return 'Выберите города или введите дистанцию вручную. Калькулятор покажет литры, стоимость топлива и цену километра.';
        }
        if (this.context === 'fuel') {
            return 'Постройте маршрут между городами или задайте километраж вручную — результат обновится сразу.';
        }
        return 'Выберите города или введите километраж вручную. Средняя цена литра подставится автоматически, а расход можно уточнить под свой автомобиль.';
    }

    private loadDefaults(preserveCurrentValues = false): void {
        this.loading = true;
        this.loadFailed = false;
        this.changeDetectorRef.markForCheck();

        this.calculatorService.getDefaults(this.vehicleClass, this.fuelType).subscribe({
            next: (defaults) => {
                this.defaults = defaults;
                if (!preserveCurrentValues) {
                    this.fuelPrice = this.normalizePositiveNumber(defaults.fuel_price) || this.fuelPrice;
                    this.consumption = this.normalizePositiveNumber(defaults.consumption_l_per_100km) || this.consumption;
                    this.persistState();
                }
                this.loading = false;
                this.changeDetectorRef.markForCheck();
            },
            error: () => {
                this.defaults = null;
                this.loadFailed = true;
                this.loading = false;
                if (!preserveCurrentValues) {
                    this.applyLocalFallback();
                    this.persistState();
                }
                this.changeDetectorRef.markForCheck();
            },
        });
    }

    registrationUrl(): string {
        return this.analytics.registrationUrl(`calculator_${this.context}`);
    }

    demoUrl(): string {
        return this.analytics.demoUrl(`calculator_${this.context}`);
    }

    trackRegistrationClick(): void {
        this.analytics.goal('registration_click', {source: `calculator_${this.context}`});
    }

    trackDemoClick(): void {
        this.analytics.goal('demo_click', {source: `calculator_${this.context}`});
    }

    private applyLocalFallback(): void {
        const fallback = FALLBACK_DEFAULTS[this.vehicleClass][this.fuelType];
        this.fuelPrice = fallback.fuelPrice;
        this.consumption = fallback.consumption;
    }

    private restoreState(): boolean {
        if (!isPlatformBrowser(this.platformId)) {
            return false;
        }

        try {
            const rawState = window.localStorage.getItem(STORAGE_KEY);
            if (!rawState) {
                return false;
            }

            const state = JSON.parse(rawState) as TripCalculatorPersistedState;
            this.vehicleClass = this.normalizeVehicleClass(state.vehicleClass || this.vehicleClass);
            this.fuelType = this.normalizeFuelType(state.fuelType || this.fuelType);
            this.distance = this.normalizePositiveNumber(state.distance) || this.distance;
            this.routeDistance = this.normalizePositiveNumber(state.routeDistance);
            this.distanceMode = state.distanceMode === 'route' ? 'route' : 'manual';
            this.roundTrip = typeof state.roundTrip === 'boolean' ? state.roundTrip : this.roundTrip;
            this.fuelPrice = this.normalizePositiveNumber(state.fuelPrice) || this.fuelPrice;
            this.consumption = this.normalizePositiveNumber(state.consumption) || this.consumption;

            return true;
        } catch {
            return false;
        }
    }

    private persistState(): void {
        if (!isPlatformBrowser(this.platformId)) {
            return;
        }

        const state: TripCalculatorPersistedState = {
            vehicleClass: this.vehicleClass,
            fuelType: this.fuelType,
            distance: this.normalizePositiveNumber(this.distance) || 100,
            routeDistance: this.normalizePositiveNumber(this.routeDistance),
            distanceMode: this.distanceMode,
            roundTrip: this.roundTrip,
            fuelPrice: this.normalizePositiveNumber(this.fuelPrice),
            consumption: this.normalizePositiveNumber(this.consumption),
        };

        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
        } catch {
        }
    }

    private normalizeVehicleClass(value: PublicTripVehicleClass | string): PublicTripVehicleClass {
        return value === 'crossover' ? 'crossover' : 'passenger';
    }

    private normalizeFuelType(value: PublicTripFuelType | string): PublicTripFuelType {
        return value === 'diesel' ? 'diesel' : 'petrol';
    }

    private normalizePositiveNumber(value: number | string | null | undefined): number {
        const numericValue = Number(value);

        if (!Number.isFinite(numericValue) || numericValue <= 0) {
            return 0;
        }

        return numericValue;
    }

    private roundMoney(value: number): number {
        return Math.round((value + Number.EPSILON) * 100) / 100;
    }

    private roundFuel(value: number): number {
        return Math.round((value + Number.EPSILON) * 100) / 100;
    }

    private pluralize(value: number, one: string, few: string, many: string): string {
        const absValue = Math.abs(value) % 100;
        const lastDigit = absValue % 10;

        if (absValue > 10 && absValue < 20) {
            return many;
        }

        if (lastDigit > 1 && lastDigit < 5) {
            return few;
        }

        if (lastDigit === 1) {
            return one;
        }

        return many;
    }

    private selectInputContent(event: FocusEvent): void {
        const target = event.target;

        if (target instanceof HTMLInputElement) {
            target.select();
        }
    }

    private trackCalculatorUse(): void {
        this.analytics.goalOnce('calculator_used', {context: this.context});
    }
}
